import { Box, Button, CircularProgress, Stack, Tooltip } from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridRowId,
  GridRowsProp,
  GridColumnVisibilityModel,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { format, isSameDay } from "date-fns";
import pluralize from "pluralize";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { HCF_USER_EVENTS } from "@src/constants/firebaseEvents";
import { ShiftInviteRequestParams, useCreateShiftInvite } from "./useCreateShiftInvite";
import { useSelector } from "react-redux";
import { SessionType } from "@src/modules/interface";
import { UseModalState, useModalState } from "@clipboard-health/ui-react";
import { zonedTimeToUtc } from "date-fns-tz";
import { v4 as uuidv4 } from "uuid";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import { useEffect, useState } from "react";
import { useShiftInviteContext } from "./Context/shiftInviteContext";
import { NearbyWorkerSearchResult } from "./Autocomplete/useGetNearbyWorkers";
import { isDefined, isString } from "@clipboard-health/util-ts";
import { message as toast } from "antd";
import { MultiShiftInviteErrorDialog } from "./Dialogs/MultiShiftInviteErrorDialog";
import { Text } from "@clipboard-health/ui-react";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { isEmpty } from "lodash";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { formatDate, formatTime } from "@clipboard-health/date-time";
export enum Severity {
  INFO = "info",
  WARN = "warn",
  ERROR = "error",
}

export interface ShiftInviteInfo {
  start: Date;
  end: Date;
  name: string;
  details?: { message: string; level: Severity };
  selectedDate?: Date;
  error?: unknown;
}

export interface GroupShiftInvite {
  workplaceId: string;
  agentReq: string;
  tmz: string;
  postShiftToMarketplace: boolean;
}

export type ShiftInviteReviewProps = {
  workerType: string;
  modalState: UseModalState;
  setActiveStep: (step: number) => void;
  selectedWorker?: NearbyWorkerSearchResult;
  customChargeRateBoost?: number;
};

type SendInviteParams = ShiftInviteRequestParams & {
  shiftInfoKey: string;
};

export function ShiftInviteReview(props: Readonly<ShiftInviteReviewProps>) {
  const { workerType, modalState, setActiveStep, selectedWorker } = props;
  const { user, admin } = useSelector((state: SessionType) => state.session);

  const { mutateAsync: createShiftInvite, isLoading: isSaving } = useCreateShiftInvite();
  const {
    groupInviteSharedData,
    multipleShiftInfo,
    multipleShiftValidationDetails,
    facilityTimeZone,
    customChargeRateBoost,
    multipleCheckShiftInviteDetails,
    logUserJourney,
    addUserJourneyEvent,
    trackUserJourneyHoverStart,
    addUserJourneyHoverEndEvent,
  } = useShiftInviteContext();
  const customChargeRateBoostIsDefined = isDefined(customChargeRateBoost);
  const excludedLevelsForPreSelect = [Severity.ERROR, Severity.WARN];
  const initialSelectedShiftInvites = [...multipleShiftInfo.keys()].filter(
    (shiftInfoKey) =>
      !excludedLevelsForPreSelect.includes(
        multipleShiftValidationDetails.get(shiftInfoKey)?.level ?? Severity.INFO
      )
  );
  const [visibilityModel, setVisibilityModel] = useState<GridColumnVisibilityModel>({
    adjustedHourlyCharge: customChargeRateBoostIsDefined,
  });
  const [selectedInvites, setSelectedInvites] = useState<GridRowId[]>(initialSelectedShiftInvites);
  const [failedInvites, setFailedInvites] = useState<ShiftInviteInfo[]>([]);
  const multiShiftInviteErrorDialogState = useModalState();
  const isShiftInviteCustomRateEnabled = useCbhFlag(CbhFeatureFlag.SHIFT_INVITE_CUSTOM_RATE_BOOST, {
    defaultValue: false,
  });

  const dataGridApiRef = useGridApiRef();

  useEffect(() => {
    dataGridApiRef.current?.autosizeColumns();
  }, [modalState, dataGridApiRef]);

  const columns: GridColDef[] = [
    {
      field: "shiftTime",
      headerName: "Shift Time",
      minWidth: 400,
      colSpan: (params) => {
        if (params.row.type === "section-header") {
          return 3;
        }
        return 1;
      },
      renderCell: (params) => {
        if (params.row.type === "section-header") {
          return (
            <Text variant="body1" bold className="section-header">
              {params.row.name}
            </Text>
          );
        }

        const { message, level } = params.value;

        return (
          <Text
            variant="subtitle2"
            color={(theme) => {
              switch (level) {
                case Severity.ERROR:
                  return theme.palette.text.secondary;
                default:
                  return theme.palette.text.primary;
              }
            }}
          >
            {message ?? ""}
          </Text>
        );
      },
      resizable: false,
      sortable: false,
      disableColumnMenu: true,
      disableReorder: true,
    },
    {
      field: "additionalNotes",
      headerName: "Additional Notes",
      minWidth: 400,
      renderCell: (params) => {
        if (!params.value) {
          return null;
        }
        const { tooltip, message, level } = params.value;

        return (
          <Tooltip
            onMouseEnter={() => {
              trackUserJourneyHoverStart();
            }}
            onMouseLeave={() => {
              addUserJourneyHoverEndEvent("Additional Notes Tooltip (Shift Invite Review)");
            }}
            title={
              <Text variant="subtitle2" sx={{ whiteSpace: "pre-line", color: "white" }}>
                {tooltip ?? message ?? ""}
              </Text>
            }
          >
            <Box>
              <Text
                variant="subtitle2"
                color={(theme) => {
                  switch (level) {
                    case Severity.ERROR:
                      return theme.palette.error.main;
                    case Severity.WARN:
                      return theme.palette.warning.main;
                    default:
                      return theme.palette.text.primary;
                  }
                }}
                sx={{
                  borderBottom: "1px dotted currentColor",
                  height: "1.5em",
                }}
              >
                {message ?? ""}
              </Text>
            </Box>
          </Tooltip>
        );
      },
      sortable: false,
      disableColumnMenu: true,
      disableReorder: true,
    },
    {
      field: "adjustedHourlyCharge",
      headerName: "Adjusted Hourly Charge",
      minWidth: 200,
      renderCell: (params) => {
        if (!params.value) {
          return null;
        }
        const { charge, chargeBreakdown, name: shiftName, start: shiftStart } = params.value;
        const tooltipLines: string[] = [];
        // Overbearing type guard due to params.value being typed as any
        if (
          customChargeRateBoostIsDefined &&
          isDefined(shiftName) &&
          isDefined(shiftStart) &&
          isDefined(charge) &&
          isDefined(chargeBreakdown) &&
          isDefined(chargeBreakdown.customRateBoost) &&
          isString(shiftStart) &&
          isString(shiftName)
        ) {
          const dayOfWeek = format(new Date(shiftStart), "EEEE");
          const nonBoostedCharge = charge - chargeBreakdown.customRateBoost;
          tooltipLines.push(
            `Charge for ${dayOfWeek} ${shiftName.toUpperCase()} shift: ${formatDollarsAsUsd(
              nonBoostedCharge
            )}`
          );
          tooltipLines.push(
            `Custom rate adjustment: ${formatDollarsAsUsd(chargeBreakdown.customRateBoost)}`
          );
          tooltipLines.push(`Total: ${formatDollarsAsUsd(charge)}`);
        }
        const tooltip = tooltipLines.map((line) => ` • ${line}`).join("\n");
        return (
          <Tooltip
            onMouseEnter={() => {
              trackUserJourneyHoverStart();
            }}
            onMouseLeave={() => {
              addUserJourneyHoverEndEvent("Adjusted Hourly Charge Tooltip (Shift Invite Review)");
            }}
            title={
              <Text variant="subtitle2" sx={{ whiteSpace: "pre", color: "white" }}>
                {tooltip}
              </Text>
            }
          >
            <Box>
              <Text
                variant="subtitle2"
                color={(theme) => theme.palette.text.primary}
                sx={{ borderBottom: "1px dotted #00000070", height: "1.5em" }}
              >
                {formatDollarsAsUsd(charge)}
              </Text>
            </Box>
          </Tooltip>
        );
      },
      sortable: false,
      disableColumnMenu: true,
      disableReorder: true,
    },
  ];

  const sortedShiftInfoEntries = Array.from(multipleShiftInfo.entries()).sort(
    (shiftInfoA, shiftInfoB) => {
      return shiftInfoA[1].start.getTime() - shiftInfoB[1].start.getTime();
    }
  );

  const allRows: GridRowsProp = sortedShiftInfoEntries.map(([shiftInfoKey, invite]) => {
    const shiftTime = `${
      formatDate(invite.start).split(",")[0]
    } ${invite.name?.toUpperCase()} shift, ${formatTime(invite.start)} - ${formatTime(invite.end)}${
      isSameDay(invite.start, invite.end) ? "" : " (next day)"
    }`;
    const validationDetails = multipleShiftValidationDetails.get(shiftInfoKey);
    const checkShiftInviteDetails = multipleCheckShiftInviteDetails.get(shiftInfoKey);
    return {
      id: shiftInfoKey,
      shiftTime: {
        message: shiftTime,
        level: validationDetails?.level,
      },
      additionalNotes: {
        tooltip: validationDetails?.tooltip,
        message: validationDetails?.message,
        level: validationDetails?.level,
      },
      adjustedHourlyCharge: {
        chargeBreakdown: checkShiftInviteDetails?.chargeBreakdown,
        charge: checkShiftInviteDetails?.charge,
        name: invite.name,
        start: checkShiftInviteDetails?.start,
      },
      enabled: validationDetails?.level !== Severity.ERROR,
    };
  });

  const segmentProperties = {
    workerType: groupInviteSharedData.agentReq,
    ...(admin ? { adminUserId: user?._id } : { workplaceUserId: user?._id }),
    ...groupInviteSharedData,
  };
  const disableCheckboxHeader = allRows.every((row) => !row.enabled);
  const SEND_MULTIPLE_INVITES = "Send Multiple Invites";

  async function sendInvite(params: SendInviteParams) {
    const { shiftInfoKey, shiftInviteRequest, queryParams } = params;
    const shiftValidationDetails = multipleShiftValidationDetails.get(shiftInfoKey);
    const workerMissingDocuments = shiftValidationDetails?.missingDocuments;
    try {
      await createShiftInvite({ shiftInviteRequest, queryParams });
      logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
        action: "GROUP_INVITE_SENT",
        groupId: shiftInviteRequest.groupId,
        skipFacilityRequiredDocumentCheck:
          isDefined(workerMissingDocuments) &&
          !isEmpty(workerMissingDocuments) &&
          shiftValidationDetails?.level === Severity.WARN,
        workerMissingDocuments,
        isOverworkingShiftsAllowed: shiftValidationDetails?.isOverworkingShift,
        ...segmentProperties,
      });
      return { invite: shiftInviteRequest };
    } catch (error: unknown) {
      logError("Error while creating the group shift invite", {
        error,
        metadata: {
          invite: { shiftInviteRequest, queryParams },
        },
      });

      logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
        action: "GROUP_INVITE_SEND_ERROR",
        groupId: shiftInviteRequest.groupId,
        ...segmentProperties,
      });

      return { invite: shiftInviteRequest, error };
    }
  }

  const rowsWithIssues = allRows.filter((row) => row.additionalNotes.level);
  const rowsWithoutIssues = allRows.filter((row) => !row.additionalNotes.level);

  const hasIssues = rowsWithIssues.length > 0;
  const hasInvitesWithoutIssues = rowsWithoutIssues.length > 0;

  const needsReviewHeaderRow = {
    id: "needs-review-header",
    type: "section-header",
    name: "Needs Review",
  };

  const readyToSendHeaderRow = {
    id: "ready-to-send-header",
    type: "section-header",
    name: "Ready to Send",
  };

  const rows = hasIssues
    ? [
        needsReviewHeaderRow,
        ...rowsWithIssues,
        ...(hasInvitesWithoutIssues ? [readyToSendHeaderRow, ...rowsWithoutIssues] : []),
      ]
    : allRows;

  return (
    <>
      <Box sx={{ height: 400 }}>
        <DataGridPro
          apiRef={dataGridApiRef}
          rows={rows}
          columns={columns}
          autosizeOnMount
          checkboxSelection
          rowSelectionModel={selectedInvites}
          onRowSelectionModelChange={(newSelectionModel) => {
            addUserJourneyEvent(
              isEmpty(newSelectionModel) ? "deselect" : "select",
              "Shift Invite Review Selection"
            );
            setSelectedInvites(newSelectionModel);
          }}
          pagination={false}
          hideFooterRowCount={isShiftInviteCustomRateEnabled}
          getRowClassName={(params) => (params.row.enabled ? "" : "Mui-disabled")}
          isRowSelectable={(params) => params.row.enabled}
          disableRowSelectionOnClick
          columnVisibilityModel={visibilityModel}
          onColumnVisibilityModelChange={(newModel) => setVisibilityModel(newModel)}
          localeText={{
            footerTotalRows: "Total Invite Count",
            footerRowSelected(count) {
              return `You have selected ${count} ${pluralize("invite", count)}`;
            },
          }}
          sx={{
            ".MuiDataGrid-columnSeparator": {
              display: "none",
            },
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: "#F5F5F5",
              color: (theme) => theme.palette.secondary.main,
            },
            "& .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
            },
            // Section headers
            "& [data-id='needs-review-header'], & [data-id='ready-to-send-header']": {
              width: "100%",
              // Hide the checkbox
              "& .MuiDataGrid-cellCheckbox": {
                display: "none",
              },
              // Add a border to the whole row and hide it on the cells since the
              // width of the cells doesn't fill the row when the checkbox is hidden
              borderBottom: "1px solid #e0e0e0",
              "& .MuiDataGrid-cell": {
                borderBottom: "none",
              },
              // Remove hover effect
              "&:hover": {
                backgroundColor: "unset",
              },
            },
            ...(disableCheckboxHeader && {
              "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
                display: "none",
              },
            }),
          }}
        />
      </Box>
      <Stack spacing={1} direction="row" justifyContent="flex-end">
        <Button
          onClick={() => {
            setActiveStep(1);
            addUserJourneyEvent("click", "Back Button (Shift Invite Review)");
            logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
              action: "GROUP_INVITE_BACK_TO_DETAILS_SECTION",
              ...segmentProperties,
            });
          }}
          variant="outlined"
          disabled={isSaving}
          startIcon={<ArrowBackIosNewOutlinedIcon />}
        >
          Back
        </Button>
        <Button
          variant="contained"
          disabled={isSaving || selectedInvites.length === 0}
          startIcon={isSaving && <CircularProgress size={18} sx={{ color: "inherit" }} />}
          onClick={async () => {
            addUserJourneyEvent("click", SEND_MULTIPLE_INVITES);
            const dataSource = new Map(selectedInvites.map((id) => [id, true]));

            const groupId = uuidv4();

            const createShiftInviteRequests = [...multipleShiftInfo.entries()]
              .filter(([shiftInfoKey, _invite]) => {
                return dataSource.has(shiftInfoKey);
              })
              .map(([shiftInfoKey, invite]) => {
                const shiftInviteRequest = {
                  workerId: selectedWorker?.workerId ?? "",
                  workplaceId: groupInviteSharedData.workplaceId,
                  shiftDetails: {
                    start: zonedTimeToUtc(invite.start, facilityTimeZone),
                    end: zonedTimeToUtc(invite.end, facilityTimeZone),
                    agentReq: workerType,
                    name: invite.name,
                    tmz: facilityTimeZone,
                    customChargeRateBoost,
                  },
                  postShiftToMarketplace: groupInviteSharedData.postShiftToMarketplace,
                  groupId,
                };
                return sendInvite({
                  shiftInviteRequest,
                  queryParams: {
                    isOverworkingShiftsAllowed: true,
                    skipFacilityRequiredDocumentCheck: true,
                  },
                  shiftInfoKey,
                });
              });

            const results = await Promise.allSettled(createShiftInviteRequests);

            const failed = results.reduce((acc, result) => {
              if (result.status === "fulfilled" && isDefined(result.value.error)) {
                acc.push({
                  ...result.value.invite.shiftDetails,
                  error: result.value.error,
                });
              }
              return acc;
            }, [] as ShiftInviteInfo[]);

            if (failed.length > 0) {
              setFailedInvites(failed);
              logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
                action: "GROUP_INVITE_SENT_PARTIAL_SUCCESSFUL",
                totalInvites: createShiftInviteRequests.length,
                failedInvites: failed.length,
                groupId,
                ...segmentProperties,
              });
              multiShiftInviteErrorDialogState.openModal();
              return;
            }

            toast.success(
              `You sent ${createShiftInviteRequests.length} shift invites to ${selectedWorker?.fullName}. You can view the invitations on your calendar.`
            );

            logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
              action: "GROUP_INVITE_SENT_SUCCESSFUL",
              totalInvites: createShiftInviteRequests.length,
              groupId,
              ...segmentProperties,
            });

            logUserJourney("submit", SEND_MULTIPLE_INVITES);
            modalState.closeModal();
          }}
        >
          Send All Selected Invites
        </Button>
      </Stack>
      <MultiShiftInviteErrorDialog
        modalState={multiShiftInviteErrorDialogState}
        failedInvites={failedInvites}
        onClose={() => {
          multiShiftInviteErrorDialogState.closeModal();
          modalState.closeModal();
        }}
      />
    </>
  );
}
